
import { Component, Vue } from 'vue-property-decorator';
import { FlowTemplateModel } from '@/models/flow_template';

interface Category {
  value: string;
  label: string;
  icon: string;
}

interface Snackbar {
  show: boolean;
  message: string;
  color: string;
}

@Component({
  name: 'ShopifyFlowTemplates',
})
export default class ShopifyFlowTemplates extends Vue {
  private loading = true;
  private templates: FlowTemplateModel[] = [];
  private searchQuery = '';
  private selectedCategory = 'all';
  private downloading: string | null = null;

  private snackbar: Snackbar = {
    show: false,
    message: '',
    color: 'success',
  };

  private categories: Category[] = [
    { value: 'all', label: 'All', icon: 'mdi-view-grid' },
    { value: 'notification', label: 'Notifications', icon: 'mdi-bell' },
    { value: 'automation', label: 'Automation', icon: 'mdi-robot' },
  ];

  private triggerIcons: Record<string, string> = {
    purchase_order_created: 'mdi-file-document-plus',
    purchase_order_confirmed: 'mdi-file-document-check',
    purchase_order_canceled: 'mdi-file-document-remove',
    purchase_order_completed: 'mdi-file-document-check-outline',
    purchase_order_received: 'mdi-package-variant-closed-check',
    purchase_order_return: 'mdi-package-variant-remove',
    inventory_low: 'mdi-alert-decagram',
  };

  // Computed property for Shopify Flow URL with store name
  private get shopifyFlowUrl(): string {
    const storeName = (window as any).SHOP_NAME || localStorage.getItem('shopify_store') || '';
    const cleanStoreName = storeName
      .trim()
      .replace(/\.myshopify\.com$/i, '')
      .replace(/^\/+|\/+$/g, '');

    if (!cleanStoreName) {
      return 'https://admin.shopify.com/apps/flow';
    }

    return `https://admin.shopify.com/store/${encodeURIComponent(cleanStoreName)}/apps/flow`;
  }

  private get filteredTemplates(): FlowTemplateModel[] {
    let result = [...this.templates];

    // Filter by category
    if (this.selectedCategory !== 'all') {
      result = result.filter((t) => t.category === this.selectedCategory);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query) ||
          t.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return result;
  }

  async mounted(): Promise<void> {
    await this.loadTemplates();
  }

  private async loadTemplates(): Promise<void> {
    try {
      this.loading = true;
      this.templates = await FlowTemplateModel.fetchAll();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to load templates:', errorMessage);
      this.showSnackbar('Failed to load templates', 'error');
    } finally {
      this.loading = false;
    }
  }

  private async downloadTemplate(template: FlowTemplateModel): Promise<void> {
    try {
      this.downloading = template.id;
      await template.download();
      this.showSnackbar(`Downloaded "${template.name}"`, 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to download template:', errorMessage);
      this.showSnackbar('Failed to download template', 'error');
    } finally {
      this.downloading = null;
    }
  }

  private getTriggerIcon(triggerType: string): string {
    return this.triggerIcons[triggerType] || 'mdi-lightning-bolt';
  }

  private getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
      notification: 'blue darken-1',
      automation: 'purple darken-1',
    };
    return colors[category] || 'grey';
  }

  private formatTriggerType(triggerType: string): string {
    return triggerType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private resetFilters(): void {
    this.searchQuery = '';
    this.selectedCategory = 'all';
  }

  private showSnackbar(message: string, color: string): void {
    this.snackbar = {
      show: true,
      message,
      color,
    };
  }
}
